import { __assign } from "tslib";
import React, { useEffect, useState, useCallback, useRef, } from "react";
import { GoogleMap, useJsApiLoader, Polygon, } from "@react-google-maps/api";
import { logEvent } from "../../logEvent";
var containerStyle = {
    width: "505px",
    height: "400px",
};
function drawGrid(map, precision) {
    var bounds = map.getBounds();
    var ne = bounds.getNorthEast(); // Top right (northeast) corner
    var sw = bounds.getSouthWest(); // Bottom left (southwest) corner
    // Convert precision to a decimal step value
    var stepSize = 1 / Math.pow(10, precision);
    var gridSquares = [];
    // Calculate the number of steps/iterations for latitude and longitude
    for (var lat = sw.lat() - 0.0001; lat < ne.lat(); lat += stepSize) {
        for (var lng = sw.lng() - 0.0001; lng < ne.lng(); lng += stepSize) {
            // Generate the square (cell) for this step
            var paths = [
                { lat: lat + stepSize, lng: lng },
                { lat: lat, lng: lng },
                { lat: lat, lng: lng + stepSize },
                { lat: lat + stepSize, lng: lng + stepSize },
            ];
            // Adjust for precision to ensure consistent grid size
            paths = paths.map(function (path) { return ({
                lat: parseFloat(path.lat.toFixed(precision)) + 0.00005,
                lng: parseFloat(path.lng.toFixed(precision)) + 0.00005,
            }); });
            gridSquares.push({
                id: "lat".concat(lat.toFixed(precision), "lng").concat(lng.toFixed(precision)),
                paths: paths,
            });
        }
    }
    // gridSquares now contains paths for drawing the grid cells
    return gridSquares;
}
function isCoordinateInDisabledLocation(coordinate, disabledLocations) {
    for (var _i = 0, disabledLocations_1 = disabledLocations; _i < disabledLocations_1.length; _i++) {
        var disabledLocation = disabledLocations_1[_i];
        var lat = disabledLocation.lat, lng = disabledLocation.lng, radius = disabledLocation.radius;
        var distance = google.maps.geometry.spherical.computeDistanceBetween(new google.maps.LatLng(coordinate.lat, coordinate.lng), new google.maps.LatLng(lat, lng));
        if (distance <= radius) {
            return true;
        }
    }
    return false;
}
function isSquareInDisabledLocation(square, disabledLocations) {
    var squareCenter = getSquareCenter(square);
    for (var _i = 0, disabledLocations_2 = disabledLocations; _i < disabledLocations_2.length; _i++) {
        var disabledLocation = disabledLocations_2[_i];
        var lat = disabledLocation.lat, lng = disabledLocation.lng, radius = disabledLocation.radius;
        var distance = google.maps.geometry.spherical.computeDistanceBetween(new google.maps.LatLng(squareCenter.lat, squareCenter.lng), new google.maps.LatLng(lat, lng));
        if (distance <= radius) {
            return true;
        }
    }
    return false;
}
function getSquareCenter(square) {
    var paths = square.paths;
    var latSum = paths.reduce(function (sum, point) { return sum + point.lat; }, 0);
    var lngSum = paths.reduce(function (sum, point) { return sum + point.lng; }, 0);
    var center = {
        lat: latSum / paths.length,
        lng: lngSum / paths.length,
    };
    return center;
}
var mapStyles = [
    {
        featureType: "all",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
    {
        featureType: "administrative.country",
        elementType: "labels",
        stylers: [{ visibility: "on" }],
    },
    {
        featureType: "administrative.province",
        elementType: "labels",
        stylers: [{ visibility: "on" }],
    },
    {
        featureType: "administrative.locality",
        elementType: "labels",
        stylers: [{ visibility: "on" }],
    },
    {
        featureType: "road",
        elementType: "labels",
        stylers: [{ visibility: "on" }],
    },
    {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }],
    },
];
var optionsDefault = {
    gestureHandling: "greedy",
    streetViewControl: false,
    styles: mapStyles,
    disableDefaultUI: true,
    zoomControl: false, // Disable zoom control
};
var Map = function (props) {
    var _a = useState({
        lat: 0,
        lng: 0,
        confirmed: false,
    }), defaultSelectedLocation = _a[0], defaultSetSelectedLocation = _a[1];
    var _b = useState(""), defaultSelectedSquareId = _b[0], defaultSetSelectedSquareId = _b[1];
    var _c = useState(-1), defaultHoverIndex = _c[0], defaultSetHoverIndex = _c[1]; // State to track hovered polygon
    var precisionDefault = 4;
    var _d = props.zoom, zoom = _d === void 0 ? 2 : _d, selectedLocation = props.selectedLocation, setSelectedLocation = props.setSelectedLocation, _e = props.center, centerProp = _e === void 0 ? { lat: 0, lng: 0 } : _e, _f = props.mapContainerStyle, mapContainerStyle = _f === void 0 ? {
        width: "100%",
        height: "250px",
    } : _f, optionsProp = props.options, _g = props.onZoomWithGrid, onZoomWithGrid = _g === void 0 ? function () { } : _g, _h = props.onZoomWithoutGrid, onZoomWithoutGrid = _h === void 0 ? function () { } : _h, _j = props.onGridSquareClicked, onGridSquareClicked = _j === void 0 ? function (squareId) { } : _j, _k = props.getMap, getMap = _k === void 0 ? function (map) { } : _k, setSelectedSquareId = props.setSelectedSquareId, selectedSquareId = props.selectedSquareId, _l = props.disableDefaultUI, disableDefaultUI = _l === void 0 ? true : _l, setHoverIndex = props.setHoverIndex, hoverIndex = props.hoverIndex, _m = props.precision, precisionProp = _m === void 0 ? 4 : _m, _o = props.disabledLocations, disabledLocations = _o === void 0 ? [] : _o;
    var setSelectedLocationHandler = setSelectedLocation || defaultSetSelectedLocation;
    var selectedLocationValue = selectedLocation || defaultSelectedLocation;
    var setSelectedSquareIdHandler = setSelectedSquareId || defaultSetSelectedSquareId;
    var selectedSquareIdValue = selectedSquareId || defaultSelectedSquareId;
    var setHoverIndexHandler = setHoverIndex || defaultSetHoverIndex;
    var hoverIndexValue = hoverIndex || defaultHoverIndex;
    var mapTypeId = "hybrid";
    var options = optionsProp || optionsDefault;
    var precision = precisionProp || precisionDefault;
    var _p = useState(centerProp), center = _p[0], setCenter = _p[1];
    var _q = useState(null), map = _q[0], setMap = _q[1];
    // const overlayRef: any = React.useRef(null);
    var _r = useState(false), showGrid = _r[0], setShowGrid = _r[1];
    var _s = useState([]), gridSquares = _s[0], setGridSquares = _s[1];
    var _t = useState(zoom), mapZoom = _t[0], setMapZoom = _t[1];
    var handlePolygonClick = function (squareId, event) {
        var _a, _b;
        logEvent("click", { target: "Map grid square" });
        onGridSquareClicked(squareId);
        setSelectedSquareIdHandler(squareId);
        var lat = (_a = event.latLng) === null || _a === void 0 ? void 0 : _a.lat();
        var lng = (_b = event.latLng) === null || _b === void 0 ? void 0 : _b.lng();
        map.panTo({ lat: lat, lng: lng });
        map.setCenter({ lat: lat, lng: lng });
        var newZoom;
        var zoomLevel = map.getZoom() || mapZoom;
        if (zoomLevel < 7) {
            // var panes: any = overlayRef.current.getPanes();
            //panes.overlayLayer.innerHTML = "";
            newZoom = zoomLevel + 3;
        }
        if (zoomLevel > 7) {
            // var panes: any = overlayRef.current.getPanes();
            //panes.overlayLayer.innerHTML = "";
            newZoom = zoomLevel;
        }
        setSelectedLocationHandler({ lat: lat, lng: lng, confirmed: false }); // Store the coordinates
        map.setZoom(newZoom);
        setMapZoom(newZoom);
    };
    var handleMouseOver = function (index) {
        setHoverIndexHandler(index);
    };
    var handleMouseOut = function () {
        setHoverIndexHandler(-1);
    };
    var onLoad = React.useCallback(function (map) {
        setMap(map);
        getMap(map);
        map.setCenter(center);
        map.setZoom(zoom);
        setMapZoom(zoom);
        // map.setOptions({
        //   draggableCursor: "default",
        //   draggingCursor: "default",
        // });
        // if (overlay) {
        // const customOverlay = new google.maps.OverlayView();
        // customOverlay.onAdd = function () {
        // var layer = document.createElement("div");
        //layer.innerHTML = `<div class="throbbing-icon"><h1>Zoom into your secret location</h1></div>`;
        //var panes: any = this.getPanes();
        //panes.overlayLayer.innerHTML = "";
        //panes.overlayLayer.appendChild(layer);
        // };
        // customOverlay.draw = function () {};
        // customOverlay.setMap(map);
        // overlayRef.current = customOverlay; // Store the overlay in the ref
        // }
    }, [
    /* any other dependencies */
    ]);
    useEffect(function () {
        if (map) {
            map.setCenter(center);
            map.setZoom(zoom);
            setMapZoom(zoom);
            var boundsChangedListener_1 = map.addListener("bounds_changed", function () {
                var newCenter = map.getCenter();
                setCenter({ lat: newCenter.lat(), lng: newCenter.lng() });
            });
            return function () { return google.maps.event.removeListener(boundsChangedListener_1); };
        }
    }, [map, zoom]);
    useEffect(function () {
        if (map && showGrid) {
            setGridSquares(drawGrid(map, precision));
        }
    }, [map, center, precision, showGrid]);
    var disabledLocationsRef = useRef(disabledLocations);
    useEffect(function () {
        disabledLocationsRef.current = disabledLocations;
    }, [disabledLocations]);
    var mapClicked = useCallback(function (e) {
        logEvent("click", { target: "Map" });
        var currentDisabledLocations = disabledLocationsRef.current;
        var zoomLevel = map.getZoom() || mapZoom;
        var zoomThreshold = getZoomThreshold();
        console.log(zoomLevel);
        if (isCoordinateInDisabledLocation({
            lat: e.latLng.lat(),
            lng: e.latLng.lng(),
        }, currentDisabledLocations) &&
            zoomLevel >= zoomThreshold)
            return;
        map.panTo({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        map.setCenter({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        var newZoom;
        if (zoomLevel < 7) {
            // var panes: any = overlayRef.current.getPanes();
            //panes.overlayLayer.innerHTML = "";
            newZoom = zoomLevel + 3;
        }
        if (zoomLevel > 7) {
            // var panes: any = overlayRef.current.getPanes();
            //panes.overlayLayer.innerHTML = "";
            newZoom = zoomLevel + 2;
        }
        if (newZoom > 20) {
            setSelectedLocationHandler({
                lat: e.latLng.lat(),
                lng: e.latLng.lng(),
                confirmed: false,
            }); // Ensure we do not exceed zoom level 20
            // if (overlay && overlayRef.current) {
            //var panes: any = overlayRef.current.getPanes();
            //var layer = document.createElement("div");
            //layer.innerHTML = `<div class="throbbing-icon-success"><h1>Done! Click next to continue.</h1></div>`;
            //panes.overlayLayer.innerHTML = "";
            //panes.overlayLayer.appendChild(layer);
            // }
        }
        map.setZoom(newZoom);
        setMapZoom(newZoom);
    }, [map, showGrid, disabledLocations]);
    useEffect(function () {
        if (map) {
            var clickListener_1 = map.addListener("click", mapClicked);
            var keyPressListener_1 = map.addListener("onKeyDown", function (e) {
                console.log(e.key);
            });
            return function () {
                google.maps.event.removeListener(clickListener_1);
                google.maps.event.removeListener(keyPressListener_1);
            };
        }
    }, [map]);
    var onUnmount = React.useCallback(function callback(map) {
        setMap(null);
    }, []);
    var getZoomThreshold = useCallback(function () {
        var zoomThreshold;
        switch (precision) {
            case 2:
                zoomThreshold = 14;
                break;
            case 3:
                zoomThreshold = 16;
                break;
            case 4:
                zoomThreshold = 18;
                break;
            case 5:
                zoomThreshold = 20;
                break;
            case 6:
                zoomThreshold = 20;
                break;
            default:
                zoomThreshold = 18;
        }
        return zoomThreshold;
    }, [precision]);
    // Function to draw the grid, modified to check zoom level
    var drawGridIfZoomedIn = function () {
        if (!map)
            return;
        var zoomLevel = map.getZoom();
        var zoomThreshold = getZoomThreshold();
        map.setMapTypeId(mapTypeId);
        setShowGrid(zoomLevel >= zoomThreshold);
        if (zoomLevel >= zoomThreshold && onZoomWithGrid) {
            onZoomWithGrid(zoomLevel);
        }
        if (zoomLevel < zoomThreshold && onZoomWithoutGrid) {
            onZoomWithoutGrid(zoomLevel);
        }
    };
    useEffect(function () {
        if (map) {
            // Attach zoom change listener
            var listener_1 = map.addListener("zoom_changed", drawGridIfZoomedIn);
            map.addListener("idle", function () {
                //logEvent("event", { target: "Map idle" });
                // if (map.getZoom() > 20 && overlayRef.current) return;
                // if (map.getZoom() > 3 && map.getZoom() < 20 && overlayRef.current) {
                //   var panes: any = overlayRef.current.getPanes();
                //   if (panes) panes.overlayLayer.innerHTML = "";
                // }
            });
            return function () { return google.maps.event.removeListener(listener_1); };
        }
    }, [map, showGrid]);
    useEffect(function () {
        // Initial check in case the map starts zoomed in
        drawGridIfZoomedIn();
    }, [map]); // Ensure this runs when the map instance becomes available
    var paths = [];
    if (selectedLocationValue.lat && selectedLocationValue.lng) {
        paths.push({
            lat: parseFloat(selectedLocationValue.lat.toFixed(precision)) + 0.00005,
            lng: parseFloat(selectedLocationValue.lng.toFixed(precision)) - 0.00005,
        });
        paths.push({
            lat: parseFloat(selectedLocationValue.lat.toFixed(precision)) - 0.00005,
            lng: parseFloat(selectedLocationValue.lng.toFixed(precision)) - 0.00005,
        });
        paths.push({
            lat: parseFloat(selectedLocationValue.lat.toFixed(precision)) - 0.00005,
            lng: parseFloat(selectedLocationValue.lng.toFixed(precision)) + 0.00005,
        });
        paths.push({
            lat: parseFloat(selectedLocationValue.lat.toFixed(precision)) + 0.00005,
            lng: parseFloat(selectedLocationValue.lng.toFixed(precision)) + 0.00005,
        });
    }
    // const options = {
    //   fillColor: "lightblue",
    //   fillOpacity: 0.5,
    //   strokeColor: "lightblue",
    //   strokeOpacity: 1,
    //   strokeWeight: 2,
    //   clickable: false,
    //   draggable: false,
    //   editable: false,
    //   geodesic: false,
    //   zIndex: 100,
    // };
    var libraries = useState([
        "places",
        "geometry",
        "drawing",
        "visualization",
    ])[0];
    var isLoaded = useJsApiLoader({
        id: "google-map-script",
        googleMapsApiKey: "AIzaSyDEbmqlzlkU3mErAG-PPdPEbTrv6opHmag",
        libraries: libraries,
    }).isLoaded;
    return (React.createElement(React.Fragment, null, isLoaded && (React.createElement(GoogleMap, __assign({ onLoad: onLoad, onUnmount: onUnmount, options: options }, props), showGrid &&
        gridSquares.map(function (square, index) {
            var isDisabled = isSquareInDisabledLocation(square, disabledLocations);
            return (React.createElement(Polygon, { key: index, paths: square.paths, options: {
                    strokeColor: selectedSquareIdValue === square.id
                        ? "lightgreen"
                        : isDisabled
                            ? "red"
                            : "#000000",
                    strokeOpacity: 0.8,
                    strokeWeight: selectedSquareIdValue === square.id ? 5 : 1,
                    fillColor: selectedSquareIdValue === square.id
                        ? "lightgreen"
                        : isDisabled
                            ? "red"
                            : "#000000",
                    fillOpacity: selectedSquareIdValue === square.id
                        ? 0.5
                        : hoverIndexValue === index
                            ? 0.3
                            : 0.1,
                    clickable: !isDisabled,
                    zIndex: selectedSquareIdValue === square.id ? 1000 : 100,
                }, onMouseOver: function () { return handleMouseOver(index); }, onMouseOut: handleMouseOut, onClick: function (e) {
                    return !isDisabled && handlePolygonClick(square.id, e);
                } }));
        })))));
};
export default React.memo(Map);
